import { describeMutationError } from "@api/mutationErrors";
import { usePrincipalKey } from "@auth/sessionStore";
import { type EmailSetup, isEmailUnavailable } from "@email/emailClient";
import {
  useDisconnectEmail,
  useEmailSetup,
  useStartEmailOauth,
  useUpdateEmailSetup,
} from "@email/emailQueries";
import {
  Button,
  FieldError,
  Input,
  SettingsCard,
  Swipe,
} from "@scribenote/brian-design-system";
import { formatDateTime } from "@utils/format-date";
import { useState } from "react";

export const EmailPanel = () => {
  const principalKey = usePrincipalKey();
  const setupQuery = useEmailSetup(principalKey);
  const setupOff = isEmailUnavailable(setupQuery.error);

  if (setupQuery.isLoading) {
    return <p className="text-sm text-annotation">Checking email…</p>;
  }

  if (setupOff) {
    return (
      <SettingsCard
        title="Email isn't available"
        subtitle="This clinic does not have the Brian email feature switched on."
      >
        <p className="text-sm text-ink-body">
          Ask Brian support to enable it before setting up a mailbox.
        </p>
      </SettingsCard>
    );
  }

  if (!setupQuery.data) {
    return (
      <FieldError>
        {describeMutationError(setupQuery.error, "load email settings")}
      </FieldError>
    );
  }

  return <SetupCard key={principalKey} setup={setupQuery.data} />;
};

const SetupCard = ({ setup }: { setup: EmailSetup }) => {
  const [mailbox, setMailbox] = useState(setup.mailbox ?? "");
  const [savedNotice, setSavedNotice] = useState(false);
  const update = useUpdateEmailSetup();
  const disconnect = useDisconnectEmail();
  const oauth = useStartEmailOauth();
  const dirty = mailbox.trim() !== (setup.mailbox ?? "");
  const savingError = update.isError
    ? describeMutationError(update.error, "save email settings")
    : undefined;

  const save = () => {
    setSavedNotice(false);
    update.mutate(
      { mailbox: mailbox.trim() },
      {
        onSuccess: (saved) => {
          setMailbox(saved.mailbox ?? "");
          setSavedNotice(true);
        },
      },
    );
  };

  return (
    <SettingsCard
      title="Brian email"
      subtitle="Connect the Google mailbox Brian will use for email."
    >
      <div className="flex max-w-2xl flex-col gap-4">
        <p className="text-sm text-annotation">
          This limited slice handles email only. SMS is not configured.
        </p>
        <Input
          type="email"
          label="Mailbox email"
          value={mailbox}
          onChange={(event) => setMailbox(event.target.value)}
          placeholder="mailbox@example.com"
          disabled={update.isPending}
        />
        {setup.prerequisites.length > 0 && (
          <div>
            <p className="text-annotation-caps text-annotation">
              Prerequisites
            </p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-ink-body">
              {setup.prerequisites.map((prerequisite) => (
                <li key={prerequisite}>{prerequisite}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={save}
            busy={update.isPending}
            disabled={!mailbox.trim() || !dirty}
          >
            Save setup
          </Button>
          {!setup.connected ? (
            <Button
              variant="accent"
              onClick={() =>
                oauth.mutate(undefined, {
                  onSuccess: ({ url }) => window.location.assign(url),
                })
              }
              busy={oauth.isPending}
              disabled={!setup.configured || !setup.mailbox || dirty}
            >
              Connect Google
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => disconnect.mutate()}
                busy={disconnect.isPending}
              >
                Disconnect Google
              </Button>
            </>
          )}
          <Swipe tone={setup.connected ? "working" : "flag"}>
            {setup.connected ? "Connected" : "Not connected"}
          </Swipe>
        </div>

        {setup.lastSyncAt && (
          <p className="text-sm text-annotation">
            Last synced {formatDateTime(setup.lastSyncAt)}
          </p>
        )}
        {savedNotice && (
          <p className="text-sm text-ink-working">Setup saved.</p>
        )}
        {savingError && <FieldError>{savingError}</FieldError>}
        {disconnect.isError && (
          <FieldError>
            {describeMutationError(disconnect.error, "disconnect Google")}
          </FieldError>
        )}
        {oauth.isError && (
          <FieldError>
            {describeMutationError(oauth.error, "start Google authorization")}
          </FieldError>
        )}
        {!setup.connected && setup.configured && setup.mailbox && (
          <p className="text-sm text-annotation">
            Google authorization is removed when you disconnect. The saved
            mailbox, messages, and drafts remain. After Google opens, return to
            Admin → Email and refresh this page when authorization is complete.
          </p>
        )}
      </div>
    </SettingsCard>
  );
};
